// Helicopter flight model (SAR-5, E-28). PURE + deterministic (ENG-1): no three /
// DOM / audio / Math.random, so it ticks identically on every peer (server-
// authoritative in MP) and replays under REP. A VTOL/hover model beside the fixed-
// wing aircraft: collective (vertical thrust vs gravity), cyclic (a body tilt that
// pushes it horizontally), pedals (yaw); NO stall. Swept-AABB collision tells a hard
// impact (crash) from a gentle vertical touchdown (landing), like the aeroplane.
import { HELI } from '../config';

/** Steering the pilot feeds the helicopter each tick. */
export interface HeliInput {
  /** Raise / lower the collective (climb / descend). */
  collectiveUp?: boolean;
  collectiveDown?: boolean;
  /** Cyclic: forward stick (+1) tilts the nose down → accelerates forward; back (−1)
   *  tilts up → decelerates / reverses. Left/right roll tilts sideways. −1..1. */
  pitch?: number;
  roll?: number;
  /** Pedals: yaw the nose (−1..1). */
  yaw?: number;
}

/** What the flight model needs from the world (ENG-1 keeps it an interface). */
export interface HeliWorld {
  isSolid(x: number, y: number, z: number): boolean;
}

export interface Helicopter {
  readonly id: number;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  /** Heading (rad, 0 = −z). Cyclic tilts are cosmetic body angles the renderer uses. */
  yaw: number; pitchTilt: number; rollTilt: number;
  /** Commanded collective 0..COLLECTIVE_MAX (vertical thrust accel). */
  collective: number;
  onGround: boolean;
  crashed: boolean;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Highest solid block-top at/below `y` under (x,z), or −Infinity if none nearby. */
function groundTopUnder(world: HeliWorld, x: number, y: number, z: number): number {
  const fx = Math.floor(x), fz = Math.floor(z), top = Math.floor(y);
  for (let cy = top; cy >= top - 6; cy--) if (world.isSolid(fx, cy, fz)) return cy + 1;
  return -Infinity;
}

/** True if the helicopter AABB centred at (x,y,z) overlaps any solid block. */
function blocked(world: HeliWorld, x: number, y: number, z: number): boolean {
  const h = HELI.HALF, hy = HELI.HALF_H;
  for (let dx = -h; dx <= h; dx += h) for (let dz = -h; dz <= h; dz += h) for (let dy = -hy; dy <= hy; dy += hy) {
    if (world.isSolid(Math.floor(x + dx), Math.floor(y + dy), Math.floor(z + dz))) return true;
  }
  return false;
}

export class HelicopterSystem {
  readonly helicopters: Helicopter[] = [];
  private nextId = 1;

  get count(): number { return this.helicopters.length; }

  spawn(x: number, y: number, z: number, yaw = 0): Helicopter {
    const hc: Helicopter = {
      id: this.nextId++, x, y, z, vx: 0, vy: 0, vz: 0, yaw, pitchTilt: 0, rollTilt: 0,
      collective: HELI.HOVER_COLLECTIVE, onGround: true, crashed: false,
    };
    this.helicopters.push(hc);
    return hc;
  }

  remove(hc: Helicopter): void { const i = this.helicopters.indexOf(hc); if (i >= 0) this.helicopters.splice(i, 1); }
  clear(): void { this.helicopters.length = 0; }

  tick(world: HeliWorld, inputFor: (hc: Helicopter) => HeliInput = () => ({})): void {
    for (const hc of this.helicopters) stepHelicopter(hc, inputFor(hc), world);
  }
}

/** Advance one helicopter one tick (SAR-5). Exported for focused unit tests. */
export function stepHelicopter(hc: Helicopter, input: HeliInput, world: HeliWorld): void {
  // ── controls ──────────────────────────────────────────────────────────────
  hc.collective = clamp(
    hc.collective + (input.collectiveUp ? HELI.COLLECTIVE_RATE : 0) - (input.collectiveDown ? HELI.COLLECTIVE_RATE : 0),
    0, HELI.COLLECTIVE_MAX,
  );
  hc.yaw += (input.yaw ?? 0) * HELI.PEDAL_RATE;
  // Cyclic tilt holds while the stick is deflected and self-centres when released.
  hc.pitchTilt = clamp(hc.pitchTilt + (input.pitch ?? 0) * HELI.CYCLIC_RATE, -HELI.CYCLIC_MAX, HELI.CYCLIC_MAX);
  hc.rollTilt = clamp(hc.rollTilt + (input.roll ?? 0) * HELI.CYCLIC_RATE, -HELI.CYCLIC_MAX, HELI.CYCLIC_MAX);
  if (!input.pitch) hc.pitchTilt *= HELI.CYCLIC_DAMP;
  if (!input.roll) hc.rollTilt *= HELI.CYCLIC_DAMP;

  // ── forces (accelerations) ──────────────────────────────────────────────────
  // Heading + right vectors in the xz-plane (yaw 0 = −z, matching the player frame).
  const fwdX = -Math.sin(hc.yaw), fwdZ = -Math.cos(hc.yaw);
  const rightX = -fwdZ, rightZ = fwdX;
  // Cyclic tilt → horizontal accel (nose-down = forward; right-roll = rightward).
  const af = HELI.CYCLIC_ACCEL * (hc.pitchTilt / HELI.CYCLIC_MAX);
  const al = HELI.CYCLIC_ACCEL * (hc.rollTilt / HELI.CYCLIC_MAX);
  hc.vx += fwdX * af + rightX * al;
  hc.vz += fwdZ * af + rightZ * al;
  // Vertical: collective thrust against gravity (hover when collective ≈ GRAVITY).
  hc.vy += hc.collective - HELI.GRAVITY;
  // Drag sheds speed (a helicopter is draggy; it doesn't glide).
  hc.vx *= HELI.DRAG_H; hc.vz *= HELI.DRAG_H; hc.vy *= HELI.DRAG_V;

  // ── ground / skids mode ─────────────────────────────────────────────────────
  const groundTop = groundTopUnder(world, hc.x, hc.y + HELI.HALF_H, hc.z);
  const agl = hc.y - HELI.HALF_H - groundTop;
  hc.onGround = Number.isFinite(groundTop) && agl <= HELI.GEAR_H;
  if (hc.onGround) {
    if (hc.vy < -HELI.CRASH_SPEED) hc.crashed = true; // a hard vertical impact is a crash (E-28)
    hc.vx *= 0.8; hc.vz *= 0.8; // skids grip
    if (hc.vy < 0) hc.vy = 0;    // rest on the surface
  }

  // ── swept-AABB integration with crash/land discrimination (E-28) ────────────
  let impact = 0;
  impact = Math.max(impact, moveAxis(hc, world, 'x', hc.vx));
  impact = Math.max(impact, moveAxis(hc, world, 'z', hc.vz));
  impact = Math.max(impact, moveAxis(hc, world, 'y', hc.vy));
  if (hc.onGround && hc.vy <= 0) { hc.y = groundTop + HELI.HALF_H; hc.vy = 0; }
  if (impact > HELI.CRASH_SPEED) hc.crashed = true;
}

/** Move one axis, blocked by solids; returns the impact speed if it hit (0 else). */
function moveAxis(hc: Helicopter, world: HeliWorld, axis: 'x' | 'y' | 'z', vel: number): number {
  if (vel === 0) return 0;
  const nx = axis === 'x' ? hc.x + vel : hc.x;
  const ny = axis === 'y' ? hc.y + vel : hc.y;
  const nz = axis === 'z' ? hc.z + vel : hc.z;
  if (blocked(world, nx, ny, nz)) {
    hc[axis === 'x' ? 'vx' : axis === 'y' ? 'vy' : 'vz'] = 0;
    return Math.abs(vel);
  }
  hc[axis] = axis === 'x' ? nx : axis === 'y' ? ny : nz;
  return 0;
}
